// @givmo/fundraisers-module — the sending-domain card (EMC-WEB3), built to Jonathan's mockups 10–12
// (Email Campaign plan, D-EMC-5): the organization's own address its campaign emails come from, the
// three CNAME records to add for it, and where they stand. It sits at the top of a campaign's Emails
// tab, and it is organization-wide: one sending domain serves every campaign.
//
// ⚠ EVERY FACT IS THE SERVER'S (`sender-identity.ts`): the state, each record and its status, the DMARC
// finding, and whether an email may be scheduled. The card says nothing while email campaigns are dark
// here (the read's `null`).
//
// The page checks the records while it is open (`useRecheckWhileOpen`): a check every two minutes, then
// every five, never while the tab is hidden. Nothing checks once the page is closed; the copy says so.

import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, CircleAlert, Copy, Info, Mail, TriangleAlert } from 'lucide-react';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  Field,
  Icon,
  IconButton,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Textarea,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { ConfirmDialog } from './confirm-dialog';
import { DialogFrame } from './layer-dialogs';
import { useFundraisersModule } from './fundraisers-context';
import { SENDING_DOMAIN_COPY as C } from './sending-domain-copy';
import {
  cnameRecords,
  dmarcDomain,
  relativeHost,
  useCreateSenderIdentity,
  useSenderIdentity,
  useSenderRecords,
  useCheckSenderRecords,
  type SenderIdentity,
  type SenderIdentityInput,
  type SenderRecord,
  type SenderRecords,
} from './sender-identity';
import { useLivePage } from './use-live-page';

const PREFIX = 'sending-domain';

/** Mailbox providers whose domain is never a sending domain: no prefill from such a sign-in. */
const MAILBOX_PROVIDERS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
]);

/**
 * Givmo's own domains, apex or any subdomain: never an organization's sending domain (the backend's
 * `GIVMO_SENDER_DOMAINS` refuses them), so a Givmo person's sign-in prefills none.
 */
const GIVMO_DOMAINS = [
  'givmo.io',
  'givmocharitable.org',
  'givfi.com',
  'givmo.com',
  'givmo.org',
  'givmo.app',
];

/** The organization's likely domain: the signed-in person's own email domain, unless a mailbox's or Givmo's. */
export function guessDomain(viewerEmail: string | null | undefined): string | null {
  const at = viewerEmail?.lastIndexOf('@') ?? -1;
  if (!viewerEmail || at < 0) return null;
  const domain = viewerEmail
    .slice(at + 1)
    .trim()
    .toLowerCase();
  const givmo = GIVMO_DOMAINS.some((own) => domain === own || domain.endsWith(`.${own}`));
  return domain && !MAILBOX_PROVIDERS.has(domain) && !givmo ? domain : null;
}

/**
 * When the records were last checked, as a clock time: "at 10:42 AM" today, "Oct 30 at 10:42 AM" before
 * (the polish-1 user test: after "Check records" a person couldn't tell a check had run).
 */
export function checkedAtText(iso: string, now: number): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'earlier';
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(
    then,
  );
  const sameDay = new Date(now).toDateString() === then.toDateString();
  if (sameDay) return `at ${time}`;
  const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(then);
  return `${day} at ${time}`;
}

/** The records written out for whoever manages the domain (the email, and "Copy all records"). */
export function recordsMessage(read: SenderRecords, orgName: string): string {
  const domain = read.domain ?? '';
  const zone = read.zone ?? domain;
  const rows = [
    ...cnameRecords(read),
    ...(read.dmarc?.status === 'needed' ? read.records.filter((r) => r.type === 'txt') : []),
  ];
  const lines = rows.map((row, index) =>
    C.mailRecord(index + 1, row.type, relativeHost(row.host, zone), row.host, row.value),
  );
  return [
    C.mailIntro(domain, zone, orgName),
    ...lines,
    ...(read.dmarc?.status === 'exists' ? [C.mailDmarcExists(domain, dmarcDomain(read))] : []),
    C.mailOutro,
  ].join('\n\n');
}

/** `mailto:` with the records written in, for the person's own email app. */
export function recordsMailto(to: string, read: SenderRecords, orgName: string): string {
  const query = new URLSearchParams({
    subject: C.mailSubject(read.domain ?? ''),
    body: recordsMessage(read, orgName),
  })
    .toString()
    // `URLSearchParams` writes a space as "+", which mail apps show literally in a mailto.
    .replace(/\+/g, '%20');
  return `mailto:${encodeURIComponent(to.trim())}?${query}`;
}

/** Re-check records while the page is open and the domain isn't ready: 2 minutes × 5, then 5. */
function useRecheckWhileOpen(active: boolean, check: () => void, busy: boolean) {
  const runs = useRef(0);
  const latest = useRef({ check, busy });
  latest.current = { check, busy };
  useEffect(() => {
    if (!active) return;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const wait = runs.current < 5 ? 120_000 : 300_000;
      timer = setTimeout(() => {
        if (!document.hidden && !latest.current.busy) {
          runs.current += 1;
          latest.current.check();
        }
        schedule();
      }, wait);
    };
    schedule();
    return () => clearTimeout(timer);
  }, [active]);
}

export function SendingDomainCard() {
  const config = useFundraisersModule();
  const identity = useSenderIdentity(config.orgRef);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  if (identity.isPending) {
    return (
      <Card testId={`${PREFIX}-loading`}>
        <CardBody className="flex flex-col gap-10">
          <Skeleton className="h-12 w-1/3" />
          <Skeleton className="h-12 w-2/3" />
        </CardBody>
      </Card>
    );
  }
  if (identity.isError) {
    return (
      <Card testId={`${PREFIX}-card`}>
        <CardBody>
          <EmptyState
            testId={`${PREFIX}-error`}
            icon={<Icon icon={CircleAlert} size={20} aria-hidden />}
            title={C.loadFailed}
            description={C.loadFailedBody}
            action={
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => void identity.refetch()}
                testId={`${PREFIX}-retry`}
              >
                {C.retry}
              </Button>
            }
          />
        </CardBody>
      </Card>
    );
  }
  const read = identity.data;
  if (!read) return null;
  // A working address asks first, and says what changing it does (the polish-1 user test).
  const change = () => (read.state === 'ready' ? setConfirming(true) : setEditing(true));

  return (
    <>
      {read.state === 'not_set_up' ? (
        <SetupCard suggestedDomain={read.suggestedDomain} onSetUp={() => setEditing(true)} />
      ) : (
        <RecordsCard identity={read} onChange={change} />
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={C.changeTitle}
        body={C.changeBody(read.fromAddress ?? read.domain ?? '')}
        confirmLabel={C.changeConfirm}
        cancelLabel={C.changeKeep}
        testId={`${PREFIX}-change-confirm-dialog`}
        confirmTestId={`${PREFIX}-change-confirm`}
        onConfirm={() => {
          setConfirming(false);
          setEditing(true);
        }}
      />
      <AddressDialog
        open={editing}
        onOpenChange={setEditing}
        identity={read.state === 'not_set_up' ? null : read}
        suggested={{ domain: read.suggestedDomain, replyTo: read.suggestedReplyTo }}
      />
    </>
  );
}

/** A tile with the card's icon, as the mockups draw it beside the title. */
function Tile({ tone, children }: { tone: 'brand' | 'success' | 'warning'; children: ReactNode }) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-36 shrink-0 items-center justify-center rounded-lg',
        tone === 'brand' && 'bg-brand-50 text-brand-700',
        tone === 'success' && 'bg-success-50 text-success-700',
        tone === 'warning' && 'bg-warning-50 text-warning-700',
      )}
    >
      {children}
    </span>
  );
}

function SetupCard({
  suggestedDomain,
  onSetUp,
}: {
  suggestedDomain: string | null;
  onSetUp: () => void;
}) {
  const config = useFundraisersModule();
  const domain = suggestedDomain ?? guessDomain(config.viewerEmail);
  return (
    <Card testId={`${PREFIX}-card`}>
      <CardBody className="flex items-start gap-16">
        <Tile tone="brand">
          <Icon icon={Mail} size={18} />
        </Tile>
        <div className="flex min-w-0 flex-col gap-4">
          <h3 className="text-s16 font-w600 text-text-strong">{C.setupTitle(domain)}</h3>
          <p className="text-s14 leading-h15 text-text-secondary">
            {C.setupBody(domain, config.orgName)}
          </p>
          <div className="mt-12">
            <Button appearance="saas" tone="primary" onClick={onSetUp} testId={`${PREFIX}-setup`}>
              {C.setupAction(domain)}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function RecordsCard({ identity, onChange }: { identity: SenderIdentity; onChange: () => void }) {
  const config = useFundraisersModule();
  const live = useLivePage();
  const records = useSenderRecords(config.orgRef, true);
  const checker = useCheckSenderRecords(config.orgRef);
  const [checkedNow, setCheckedNow] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const read = records.data ?? null;
  const domain = read?.domain ?? identity.domain ?? '';
  // Where the records go: the registrable domain, which a subdomain sender's DNS is kept under.
  const zone = read?.zone ?? domain;
  // Some DNS screens want each record's full name rather than the part before the zone.
  const [fullNames, setFullNames] = useState(false);
  const ready = identity.state === 'ready';
  const lapsed = identity.state === 'lapsed';
  const gone = read?.providerDomainMissing === true;

  // The "Last checked" line moves on with the clock.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const check = (byPerson: boolean) =>
    checker.mutate(undefined, {
      onSuccess: (answer) => {
        if (!live()) return;
        setNow(Date.now());
        if (byPerson) setCheckedNow(true);
        if (answer?.state === 'ready' && !ready) toast.success(C.readyToast(domain));
      },
      onError: (error) => {
        if (live() && byPerson)
          toast.error(isGivmoError(error) && error.message ? error.message : C.checkFailed);
      },
    });
  useRecheckWhileOpen(!ready && !gone && read !== null, () => check(false), checker.isPending);

  const cnames = read ? cnameRecords(read) : [];
  const dmarcRow = read?.records.find((row) => row.type === 'txt') ?? null;
  // Each row's status shows once any check has run, the person's own included.
  const checked = ready || read?.lastCheckedAt != null || checkedNow;

  if (gone) {
    return (
      <Card testId={`${PREFIX}-card`}>
        <CardBody className="flex items-start gap-16">
          <Tile tone="warning">
            <Icon icon={TriangleAlert} size={18} />
          </Tile>
          <div className="flex min-w-0 flex-col gap-4">
            <h3 className="text-s16 font-w600 text-text-strong">{C.goneTitle(domain)}</h3>
            <p className="text-s14 leading-h15 text-text-secondary">{C.goneBody(domain)}</p>
            <div className="mt-12">
              <Button
                appearance="saas"
                tone="primary"
                onClick={onChange}
                testId={`${PREFIX}-setup-again`}
              >
                {C.goneAction}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  const header = ready ? (
    <div className="flex items-start gap-16">
      <Tile tone="success">
        <Icon icon={Check} size={18} />
      </Tile>
      <div className="flex min-w-0 flex-col gap-4">
        <div className="flex flex-wrap items-baseline gap-x-10">
          <h3 className="text-s16 font-w600 text-text-strong" {...testIdProp(`${PREFIX}-ready`)}>
            {C.readyTitle(identity.fromAddress ?? domain)}
          </h3>
          <Button
            appearance="saas"
            tone="link"
            size="sm"
            className="h-auto p-0"
            onClick={onChange}
            testId={`${PREFIX}-change`}
          >
            {C.change}
          </Button>
        </div>
        <p className="text-s14 leading-h15 text-text-secondary">
          {C.readyBody(config.orgName, domain)}
        </p>
        {identity.postalAddress && (
          <p
            className="text-s13 leading-h15 text-text-tertiary"
            {...testIdProp(`${PREFIX}-footer-address`)}
          >
            {C.footerAddress(identity.postalAddress.split('\n').join(', '))}
          </p>
        )}
      </div>
    </div>
  ) : lapsed ? (
    <div className="flex items-start gap-16">
      <Tile tone="warning">
        <Icon icon={TriangleAlert} size={18} />
      </Tile>
      <div className="flex min-w-0 flex-col gap-4">
        <h3 className="text-s16 font-w600 text-text-strong" {...testIdProp(`${PREFIX}-lapsed`)}>
          {C.lapsedTitle(domain)}
        </h3>
        <p className="text-s14 leading-h15 text-text-secondary">{C.lapsedBody(domain)}</p>
      </div>
    </div>
  ) : (
    <div className="flex flex-col gap-4">
      <h3 className="text-s16 font-w600 text-text-strong" {...testIdProp(`${PREFIX}-pending`)}>
        {C.recordsTitle(domain)}
      </h3>
      <p className="text-s13 leading-h15 text-text-secondary">{C.recordsBody(zone)}</p>
    </div>
  );

  return (
    <Card testId={`${PREFIX}-card`}>
      <div
        className={cn(
          'px-20 pt-20',
          !ready && !lapsed && 'border-b border-border-subtle pb-16',
          (ready || lapsed) && 'pb-16',
        )}
      >
        {header}
      </div>
      <CardBody className="flex flex-col gap-16">
        {!ready && (
          <div className="flex flex-col gap-4">
            <p className="flex flex-wrap items-baseline gap-x-8 text-s14 text-text-secondary">
              <span>{C.fromLead}</span>
              <span className="font-w600 text-text-strong" {...testIdProp(`${PREFIX}-from`)}>
                {identity.displayName} &lt;{identity.fromAddress}&gt;
              </span>
              <Button
                appearance="saas"
                tone="link"
                size="sm"
                className="h-auto p-0"
                onClick={onChange}
                testId={`${PREFIX}-change`}
              >
                {C.change}
              </Button>
            </p>
            <p className="text-s13 text-text-tertiary">{C.forAll(config.orgName)}</p>
          </div>
        )}

        {records.isPending ? (
          <div className="flex flex-col gap-8">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : !read ? (
          <EmptyState
            testId={`${PREFIX}-records-error`}
            icon={<Icon icon={CircleAlert} size={20} aria-hidden />}
            title={C.loadFailed}
            description={C.loadFailedBody}
            action={
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => void records.refetch()}
                testId={`${PREFIX}-records-retry`}
              >
                {C.retry}
              </Button>
            }
          />
        ) : (
          <>
            <RecordsTable
              rows={cnames}
              zone={zone}
              fullNames={fullNames && !ready}
              statusShown={checked}
              lapsed={lapsed}
              copyable={!ready}
            />
            {!ready && (
              <p
                className="-mt-8 flex flex-wrap items-baseline gap-x-6 text-s12 text-text-tertiary"
                {...testIdProp(`${PREFIX}-zone-note`)}
              >
                <span>{fullNames ? C.zoneNoteFull(zone) : C.zoneNote(zone)}</span>
                <Button
                  appearance="saas"
                  tone="link"
                  size="sm"
                  className="h-auto p-0 text-s12"
                  aria-pressed={fullNames}
                  onClick={() => setFullNames((on) => !on)}
                  testId={`${PREFIX}-full-names`}
                >
                  {fullNames ? C.shortNamesAction : C.fullNamesAction}
                </Button>
              </p>
            )}
            <DmarcLine
              read={read}
              domain={domain}
              zone={zone}
              fullNames={fullNames && !ready}
              row={dmarcRow}
            />
          </>
        )}

        {!ready && read && (
          <SendAndCheck
            read={read}
            zone={zone}
            checking={checker.isPending}
            onCheck={() => check(true)}
            lastChecked={
              read.lastCheckedAt ? C.lastChecked(checkedAtText(read.lastCheckedAt, now)) : null
            }
            notYet={checkedNow && !checker.isPending && read.state !== 'ready'}
          />
        )}
      </CardBody>
    </Card>
  );
}

function RecordsTable({
  rows,
  zone,
  fullNames,
  statusShown,
  lapsed,
  copyable,
}: {
  rows: SenderRecord[];
  zone: string;
  fullNames: boolean;
  statusShown: boolean;
  lapsed: boolean;
  copyable: boolean;
}) {
  const live = useLivePage();
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      if (live()) toast.success(C.copied);
    } catch {
      if (live()) toast.error(C.copyFailed);
    }
  };
  return (
    <div className="overflow-hidden rounded-lg border border-border-subtle">
      <Table testId={`${PREFIX}-records`} className="text-s13">
        <THead>
          <TR>
            <TH className="w-[76px]">{C.type}</TH>
            <TH className="w-[36%]">{C.name}</TH>
            <TH>{C.value}</TH>
            {statusShown && <TH className="w-[104px] text-right">{C.status}</TH>}
            {copyable && !statusShown && <TH className="w-[52px]" aria-hidden />}
          </TR>
        </THead>
        <TBody>
          {rows.map((row) => {
            const short = fullNames ? row.host : relativeHost(row.host, zone);
            return (
              <TR key={row.key} {...testIdProp(`${PREFIX}-record-${row.key}`)}>
                <TD className="h-[46px] font-mono text-s12 font-w600 text-text-secondary">
                  {row.type.toUpperCase()}
                </TD>
                <TD className="h-[46px]">
                  <span className="inline-flex max-w-full items-center gap-6">
                    <span
                      className="font-mono text-s13 text-text-strong [overflow-wrap:anywhere]"
                      title={row.host}
                    >
                      <DotBreaks text={short} />
                      {short !== row.host && (
                        <span className="text-text-tertiary">
                          <DotBreaks text={row.host.slice(short.length)} />
                        </span>
                      )}
                    </span>
                    {copyable && (
                      <IconButton
                        tone="ghost"
                        size="sm"
                        aria-label={C.copyName(row.host)}
                        onClick={() => void copy(short)}
                        testId={`${PREFIX}-copy-name-${row.key}`}
                      >
                        <Icon icon={Copy} size={14} />
                      </IconButton>
                    )}
                  </span>
                </TD>
                <TD className="h-[46px]">
                  <span className="flex w-full items-center justify-between gap-8">
                    <span className="font-mono text-s13 text-text-strong [overflow-wrap:anywhere]">
                      <DotBreaks text={row.value} />
                    </span>
                    {copyable && !statusShown && (
                      <IconButton
                        tone="ghost"
                        size="sm"
                        aria-label={C.copyValue(row.host)}
                        onClick={() => void copy(row.value)}
                        testId={`${PREFIX}-copy-value-${row.key}`}
                      >
                        <Icon icon={Copy} size={14} />
                      </IconButton>
                    )}
                  </span>
                </TD>
                {statusShown && (
                  <TD className="h-[46px] text-right">
                    <span className="inline-flex items-center gap-6">
                      {copyable && (
                        <IconButton
                          tone="ghost"
                          size="sm"
                          aria-label={C.copyValue(row.host)}
                          onClick={() => void copy(row.value)}
                          testId={`${PREFIX}-copy-value-${row.key}`}
                        >
                          <Icon icon={Copy} size={14} />
                        </IconButton>
                      )}
                      <RecordStatus record={row} lapsed={lapsed} />
                    </span>
                  </TD>
                )}
              </TR>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}

/** A host or value that may wrap only after a dot, never mid-label ("sendgrid.ne / t"). */
function DotBreaks({ text }: { text: string }) {
  const parts = text.split('.');
  return (
    <>
      {parts.map((part, index) => (
        <Fragment key={index}>
          {part}
          {index < parts.length - 1 && (
            <>
              .<wbr />
            </>
          )}
        </Fragment>
      ))}
    </>
  );
}

function RecordStatus({ record, lapsed }: { record: SenderRecord; lapsed: boolean }) {
  if (record.status === 'valid')
    return (
      <span
        className="inline-flex items-center gap-4 whitespace-nowrap font-w500 text-success-700"
        {...testIdProp(`${PREFIX}-status-${record.key}`)}
      >
        <Icon icon={Check} size={14} aria-hidden />
        {C.found}
      </span>
    );
  if (record.status === 'not_valid')
    return (
      <span
        className="whitespace-nowrap font-w500 text-warning-700"
        title={record.reason ?? undefined}
        {...testIdProp(`${PREFIX}-status-${record.key}`)}
      >
        {lapsed ? C.missingRecord : C.notFoundYet}
      </span>
    );
  return <span className="text-text-tertiary">–</span>;
}

function DmarcLine({
  read,
  domain,
  zone,
  fullNames,
  row,
}: {
  read: SenderRecords;
  domain: string;
  zone: string;
  fullNames: boolean;
  row: SenderRecord | null;
}) {
  if (!read.dmarc) return null;
  const status = read.dmarc.status;
  return (
    <div className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-dmarc`)}>
      <p className="flex items-start gap-8 text-s13 leading-h15 text-text-secondary">
        <Icon icon={Info} size={16} aria-hidden className="mt-1 shrink-0 text-text-tertiary" />
        <span {...testIdProp(`${PREFIX}-dmarc-${status}`)}>
          {status === 'exists'
            ? C.dmarcExists(domain, dmarcDomain(read))
            : status === 'needed'
              ? C.dmarcNeeded(domain)
              : C.dmarcUnknown(domain)}
        </span>
      </p>
      {status === 'needed' && row && (
        <RecordsTable
          rows={[row]}
          zone={zone}
          fullNames={fullNames}
          statusShown={false}
          lapsed={false}
          copyable
        />
      )}
    </div>
  );
}

function SendAndCheck({
  read,
  zone,
  checking,
  onCheck,
  lastChecked,
  notYet,
}: {
  read: SenderRecords;
  zone: string;
  checking: boolean;
  onCheck: () => void;
  lastChecked: string | null;
  notYet: boolean;
}) {
  const config = useFundraisersModule();
  const live = useLivePage();
  const [to, setTo] = useState('');
  const mailto = useMemo(() => recordsMailto(to, read, config.orgName), [to, read, config.orgName]);
  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(recordsMessage(read, config.orgName));
      if (live()) toast.success(C.copiedAll);
    } catch {
      if (live()) toast.error(C.copyFailed);
    }
  };
  return (
    <div className="flex flex-col gap-10 border-t border-border-subtle pt-16">
      <div className="flex flex-wrap items-end gap-12">
        <div className="flex min-w-0 flex-1 flex-wrap items-end gap-10">
          <Field
            label={C.sendLead(zone)}
            type="email"
            value={to}
            placeholder={C.sendPlaceholder(zone)}
            onChange={(event) => setTo(event.target.value)}
            containerClassName="min-w-[220px] max-w-[300px] flex-1"
            testId={`${PREFIX}-send-to`}
          />
          <div className="flex items-center gap-10">
            <Button
              appearance="saas"
              tone="primary"
              onClick={() => window.location.assign(mailto)}
              testId={`${PREFIX}-send`}
            >
              {C.sendAction}
            </Button>
            <Button
              appearance="saas"
              tone="link"
              size="sm"
              onClick={() => void copyAll()}
              testId={`${PREFIX}-copy-all`}
            >
              {C.copyAll}
            </Button>
          </div>
        </div>
        <Button
          appearance="saas"
          tone="secondary"
          loading={checking}
          onClick={onCheck}
          testId={`${PREFIX}-check`}
        >
          {checking ? C.checking : C.check}
        </Button>
      </div>
      <p
        role="status"
        className="text-s12 text-text-tertiary"
        {...testIdProp(`${PREFIX}-check-line`)}
      >
        {notYet ? C.notYet : C.sendNote}
        {lastChecked && <> {lastChecked}</>}
      </p>
    </div>
  );
}

/** The address picker: the From address, the name people see, Reply-To and the footer's address. */
function AddressDialog({
  open,
  onOpenChange,
  identity,
  suggested,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  identity: SenderIdentity | null;
  /** The organization's own website domain and contact email, from its profile (the server's). */
  suggested: { domain: string | null; replyTo: string | null };
}) {
  const config = useFundraisersModule();
  const live = useLivePage();
  const create = useCreateSenderIdentity(config.orgRef);
  const blank = (): SenderIdentityInput => ({
    // The organization's website domain first; the person's own email domain only when the
    // profile has none (never a mailbox provider's or Givmo's).
    domain: identity?.domain ?? suggested.domain ?? guessDomain(config.viewerEmail) ?? '',
    from_local_part: identity?.fromLocalPart ?? 'appeal',
    display_name: identity?.displayName ?? config.orgName,
    // The organization's contact email, else nothing: never the signed-in person's own address,
    // or donors' replies would reach whoever set the domain up (the KCC dev build, 2026-10-31).
    reply_to: identity?.replyTo ?? suggested.replyTo ?? '',
    postal_address: identity?.postalAddress ?? '',
  });
  const [form, setForm] = useState<SenderIdentityInput>(blank);
  const [confirmDomain, setConfirmDomain] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // Each opening starts from what is saved.
  useEffect(() => {
    if (open) {
      setForm(blank());
      setConfirmDomain(false);
      setProblem(null);
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (key: keyof SenderIdentityInput) => (value: string) =>
    setForm((now) => ({ ...now, [key]: value }));
  const onlyPostalMissing =
    !form.postal_address.trim() &&
    Boolean(
      form.domain.trim() &&
      form.from_local_part.trim() &&
      form.display_name.trim() &&
      form.reply_to.trim(),
    );
  const complete =
    form.domain.trim() &&
    form.from_local_part.trim() &&
    form.display_name.trim() &&
    form.reply_to.trim() &&
    form.postal_address.trim();

  const submit = () => {
    if (!complete) {
      setProblem(C.missing);
      return;
    }
    setProblem(null);
    create.mutate(
      {
        domain: form.domain.trim(),
        from_local_part: form.from_local_part.trim(),
        display_name: form.display_name.trim(),
        reply_to: form.reply_to.trim(),
        postal_address: form.postal_address.trim(),
        ...(confirmDomain ? { confirm_domain_change: true } : {}),
      },
      {
        onSuccess: () => {
          if (live()) onOpenChange(false);
        },
        onError: (error) => {
          if (!live()) return;
          if (
            isGivmoError(error) &&
            firstErrorCode(error) === 'SENDER_DOMAIN_CHANGE_NEEDS_CONFIRMATION'
          ) {
            setConfirmDomain(true);
            return;
          }
          setProblem(isGivmoError(error) && error.message ? error.message : C.failed);
        },
      },
    );
  };

  return (
    <DialogFrame
      open={open}
      onOpenChange={onOpenChange}
      title={C.dialogTitle}
      description={C.dialogBody}
      testId={`${PREFIX}-dialog`}
      width={520}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={() => onOpenChange(false)}
            testId={`${PREFIX}-dialog-cancel`}
          >
            {C.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={create.isPending}
            // Every field is required (the footer's mailing address too: the backend refuses an email
            // without one), so the act waits until they are filled (the polish-1 user test).
            disabled={!complete || create.isPending}
            onClick={submit}
            testId={`${PREFIX}-dialog-save`}
          >
            {confirmDomain
              ? C.changeDomainAction(form.domain.trim())
              : identity
                ? C.save
                : C.continue}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-14">
        <div className="flex flex-col gap-6">
          <span className="text-s13 font-w500 text-text-secondary">{C.addressLabel}</span>
          <div className="flex items-center gap-6">
            <Field
              aria-label={`${C.addressLabel}, before the @`}
              value={form.from_local_part}
              placeholder={C.localPlaceholder}
              onChange={(event) => set('from_local_part')(event.target.value)}
              containerClassName="w-[38%]"
              testId={`${PREFIX}-local`}
            />
            <span className="text-s14 text-text-secondary">@</span>
            <Field
              aria-label={`${C.addressLabel}, the domain`}
              value={form.domain}
              placeholder={C.domainPlaceholder}
              onChange={(event) => {
                set('domain')(event.target.value);
                setConfirmDomain(false);
              }}
              containerClassName="flex-1"
              testId={`${PREFIX}-domain`}
            />
          </div>
          <span className="text-s12 text-text-tertiary">{C.addressHelper}</span>
        </div>
        <Field
          label={C.nameLabel}
          value={form.display_name}
          onChange={(event) => set('display_name')(event.target.value)}
          testId={`${PREFIX}-name`}
        />
        <Field
          label={C.replyLabel}
          type="email"
          value={form.reply_to}
          placeholder={C.replyPlaceholder(form.domain.trim() || null)}
          helper={C.replyHelper}
          onChange={(event) => set('reply_to')(event.target.value)}
          testId={`${PREFIX}-reply`}
        />
        <div className="flex flex-col gap-6">
          <label htmlFor={`${PREFIX}-postal`} className="text-s13 font-w500 text-text-secondary">
            {C.postalLabel}
          </label>
          <Textarea
            id={`${PREFIX}-postal`}
            rows={3}
            value={form.postal_address}
            onChange={(event) => set('postal_address')(event.target.value)}
            testId={`${PREFIX}-postal`}
          />
          <span className="text-s12 text-text-tertiary">{C.postalHelper}</span>
        </div>
        {confirmDomain && (
          <p
            className="flex items-start gap-8 rounded-lg border border-warning-100 bg-warning-50 p-12 text-s13 leading-h15 text-text-strong"
            {...testIdProp(`${PREFIX}-confirm-domain`)}
          >
            <Icon icon={TriangleAlert} size={16} aria-hidden className="mt-1 shrink-0" />
            {C.changeDomainNote(form.domain.trim())}
          </p>
        )}
        {problem ? (
          <p className="text-s13 text-danger-700" role="alert" {...testIdProp(`${PREFIX}-problem`)}>
            {problem}
          </p>
        ) : (
          !complete && (
            <p className="text-s13 text-text-secondary" {...testIdProp(`${PREFIX}-incomplete`)}>
              {onlyPostalMissing ? C.missingPostal : C.missing}
            </p>
          )
        )}
      </div>
    </DialogFrame>
  );
}

/** The Emails tab's body until EMC-WEB1 lands: the card, then mockups 10 and 12's empty state. */
export function EmailsTabBody() {
  const config = useFundraisersModule();
  const identity = useSenderIdentity(config.orgRef);
  const ready = identity.data?.state === 'ready';
  const domain =
    identity.data?.domain ?? identity.data?.suggestedDomain ?? guessDomain(config.viewerEmail);
  // Dark here (the read's route miss), or not read yet: nothing, even at the tab's own address.
  if (identity.data == null) return null;
  return (
    <div className="flex flex-col gap-16" {...testIdProp('fundraiser-emails')}>
      <SendingDomainCard />
      <Card>
        <CardBody>
          <EmptyState
            testId="fundraiser-emails-empty"
            icon={<Icon icon={Mail} size={20} aria-hidden />}
            title={C.emptyTitle}
            description={
              <span className="block text-balance">
                {ready ? C.emptyAfterDomain : C.emptyBeforeDomain(domain)}
              </span>
            }
          />
        </CardBody>
      </Card>
    </div>
  );
}
